import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert,
  LinearProgress,
  Avatar,
} from '@mui/material';
import { BarChart as BarChartIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { enhancedApi } from '../api';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import ReportPeriodToolbar from '../components/reports/ReportPeriodToolbar';
import { rangeFromPreset } from '../components/reports/reportDatePresets';

dayjs.extend(quarterOfYear);

const fmtHrs = (n) => (n != null && !isNaN(n) ? `${Number(n).toFixed(1)}` : '—');
const fmtPercent = (n) => (n != null && !isNaN(n) ? `${Number(n).toFixed(1)}%` : '—');
const fmtCurrency = (n) =>
  n != null && !isNaN(n) ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';

/** Data Grid v7+ passes the cell value; older API used `{ value }` — normalize both (null-safe). */
const cellVal = (p) => {
  if (p == null) return p;
  if (typeof p === 'object' && 'value' in p) return p.value;
  return p;
};

function staffInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  return parts[0][0].toUpperCase();
}

/** @param {number} utilPct */
function utilizationMuiColor(utilPct) {
  if (utilPct > 80) return 'success';
  if (utilPct >= 60) return 'warning';
  return 'error';
}

const StaffUtilization = () => {
  const [datePreset, setDatePreset] = useState('this_month');
  const [dateFrom, setDateFrom] = useState(() => rangeFromPreset('this_month').from);
  const [dateTo, setDateTo] = useState(() => rangeFromPreset('this_month').to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyPreset = useCallback((preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const { from, to } = rangeFromPreset(preset);
      setDateFrom(from);
      setDateTo(to);
    }
  }, []);

  const fetchReport = useCallback(() => {
    setLoading(true);
    setError(null);
    enhancedApi
      .getStaffUtilization({
        date_from: dateFrom.format('YYYY-MM-DD'),
        date_to: dateTo.format('YYYY-MM-DD'),
      })
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load report'))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const rows = useMemo(() => {
    const items = data?.staff ?? data?.rows ?? [];
    return items.map((r, i) => {
      const totalHours = r.total_hours ?? r.hours_logged ?? r.hours ?? 0;
      const billableHours = r.billable_hours ?? r.billable ?? 0;
      const nonBillable = r.non_billable_hours ?? Math.max(0, totalHours - billableHours);
      const targetHours = r.target_hours ?? r.target ?? 0;
      const utilPct = r.utilization_percent != null
        ? Number(r.utilization_percent)
        : targetHours > 0
          ? Math.min((totalHours / targetHours) * 100, 100)
          : 0;
      const revenueGenerated = r.revenue_generated ?? r.revenue ?? null;
      return {
        id: r.user_id ?? r.id ?? i,
        staffMember: r.name ?? r.staff_member ?? r.display_name ?? '—',
        billableHours,
        nonBillable,
        utilPct,
        revenueGenerated,
        totalHours,
        targetHours,
      };
    });
  }, [data?.staff, data?.rows]);

  const columns = useMemo(
    () => [
      { field: 'staffMember', headerName: 'Staff', flex: 1, minWidth: 140 },
      {
        field: 'billableHours',
        headerName: 'Billable Hrs',
        width: 120,
        valueFormatter: (p) => fmtHrs(cellVal(p)),
      },
      {
        field: 'nonBillable',
        headerName: 'Non-Billable',
        width: 120,
        valueFormatter: (p) => fmtHrs(cellVal(p)),
      },
      {
        field: 'utilPct',
        headerName: 'Utilization %',
        width: 120,
        valueFormatter: (p) => fmtPercent(cellVal(p)),
      },
      {
        field: 'revenueGenerated',
        headerName: 'Revenue Generated',
        width: 150,
        valueFormatter: (p) => {
          const v = cellVal(p);
          return v != null ? fmtCurrency(v) : '—';
        },
      },
    ],
    []
  );

  const empty = !loading && !error && rows.length === 0;

  const handleExportCsv = () => {
    const header = 'Staff,Billable Hrs,Non-Billable,Utilization %,Revenue Generated';
    const body = rows
      .map((r) =>
        [
          r.staffMember,
          r.billableHours,
          r.nonBillable,
          r.utilPct != null ? r.utilPct.toFixed(1) : '',
          r.revenueGenerated ?? '',
        ].join(',')
      )
      .join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff-utilization-${dateFrom.format('YYYY-MM-DD')}-${dateTo.format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <ReportPeriodToolbar
        title="Staff Utilization"
        subtitle="Billable effort versus period targets from time entries."
        datePreset={datePreset}
        onDatePresetChange={applyPreset}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={(v) => {
          if (v) {
            setDateFrom(v);
            setDatePreset('custom');
          }
        }}
        onDateToChange={(v) => {
          if (v) {
            setDateTo(v);
            setDatePreset('custom');
          }
        }}
        onExport={handleExportCsv}
        exportDisabled={loading || empty}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <>
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2, mb: 3 }} />
          <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
        </>
      ) : empty ? (
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
            borderColor: 'divider',
            borderStyle: 'dashed',
          }}
        >
          <BarChartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
            No data for selected period
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No time entries for {dateFrom.format('MMM D, YYYY')} – {dateTo.format('MMM D, YYYY')}. Try widening the range.
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Utilization vs target
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {rows.map((r) => {
                const barPct = Math.min(Math.max(r.utilPct, 0), 100);
                const color = utilizationMuiColor(r.utilPct);
                return (
                  <Box key={r.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                      <Avatar
                        sx={{ width: 32, height: 32, fontSize: '0.8125rem', bgcolor: 'primary.light', color: 'primary.dark' }}
                      >
                        {staffInitials(r.staffMember)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap title={r.staffMember}>
                        {r.staffMember}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {r.totalHours.toFixed(1)} hrs / {r.targetHours.toFixed(1)} hrs target
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, width: 48, textAlign: 'right' }}>
                        {fmtPercent(r.utilPct)}
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={barPct} color={color} sx={{ height: 8, borderRadius: 1 }} />
                  </Box>
                );
              })}
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ borderColor: 'divider', overflow: 'hidden' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              sx={{ border: 'none', '& .MuiDataGrid-cell': { borderColor: 'divider' } }}
              autoHeight
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default StaffUtilization;
